import React, { useState, useRef } from 'react';
import { MessageSquare, Image, X } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '../shared/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../services/supabaseClient';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = ['Bug Report', 'Feature Request', 'Question', 'Other'] as const;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { darkMode } = useTheme();
  const [category, setCategory] = useState<string>(CATEGORIES[0]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotName, setScreenshotName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, etc.)');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Image must be under 5MB');
      return;
    }

    setError(null);
    setScreenshotName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      setScreenshot(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotName('');
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error: fnError } = await supabase.functions.invoke('send-feedback', {
        body: {
          category,
          subject: subject.trim(),
          description: description.trim(),
          screenshot: screenshot || undefined,
          screenshotName: screenshotName || undefined,
        },
      });

      if (fnError) throw fnError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setCategory(CATEGORIES[0]);
        setSubject('');
        setDescription('');
        setScreenshot(null);
        setScreenshotName('');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send feedback');
      setIsSubmitting(false);
    }
  };

  const inputClasses = `w-full px-3 py-2 rounded-xl text-sm transition-colors ${
    darkMode
      ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-violet-500/50'
      : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-violet-300'
  } focus:outline-none focus:ring-1 focus:ring-purple-500/30`;

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting ? () => {} : onClose} maxWidth="sm">
      <ModalContent>
        <ModalHeader
          title="Submit Feedback"
          icon={
            <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center ${
              darkMode ? 'bg-violet-500/20' : 'bg-violet-100'
            }`}>
              <MessageSquare size={28} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
            </div>
          }
        />

        {success ? (
          <div className={`text-center p-4 rounded-xl ${
            darkMode ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'
          }`}>
            <p className={`text-sm font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
              Thank you! Your feedback has been sent.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={inputClasses}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of your feedback"
                  className={inputClasses}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us more..."
                  rows={4}
                  className={`${inputClasses} resize-none`}
                />
              </div>

              {/* Screenshot attachment */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Screenshot <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>(optional)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {screenshot ? (
                  <div className={`relative rounded-xl overflow-hidden border ${
                    darkMode ? 'border-white/10' : 'border-slate-200'
                  }`}>
                    <img
                      src={screenshot}
                      alt="Screenshot preview"
                      className="w-full max-h-40 object-cover"
                    />
                    <button
                      onClick={removeScreenshot}
                      className={`absolute top-2 right-2 p-1 rounded-lg ${
                        darkMode
                          ? 'bg-slate-900/80 text-slate-300 hover:bg-slate-900'
                          : 'bg-white/80 text-slate-600 hover:bg-white'
                      }`}
                    >
                      <X size={14} />
                    </button>
                    <div className={`px-3 py-1.5 text-xs truncate ${
                      darkMode ? 'bg-slate-800/80 text-slate-400' : 'bg-slate-50 text-slate-500'
                    }`}>
                      {screenshotName}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl border-2 border-dashed transition-colors ${
                      darkMode
                        ? 'border-white/10 text-slate-400 hover:border-violet-500/30 hover:text-violet-400'
                        : 'border-slate-200 text-slate-400 hover:border-violet-300 hover:text-violet-600'
                    }`}
                  >
                    <Image size={16} />
                    <span className="text-sm">Attach a screenshot</span>
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <ModalFooter>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                  darkMode
                    ? 'bg-white/10 hover:bg-white/20 text-white'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !subject.trim() || !description.trim()}
                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-colors ${
                  isSubmitting || !subject.trim() || !description.trim()
                    ? 'bg-violet-500/50 cursor-not-allowed'
                    : 'bg-violet-600 hover:bg-violet-700'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Feedback'}
              </button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
